// src/components/homeComponents/WeDoParallax.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Section = styled.section`
  height: ${(p) => p.$vhHeight};
  position: relative;
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  justify-content: center; /* horizontal centering */
  align-items: center; /* vertical centering */
`;

const TextRow = styled.div`
  display: flex;
  align-items: center;
  font-weight: 800;
  font-size: 8vw; /* responsive size using vw */
  gap: 2vw;
  line-height: 1;
`;

const Static = styled.div`
  color: #ffffff;
  flex-shrink: 0;
`;

const DynamicViewport = styled.div`
  height: 1.2em; /* just one line tall (relative to font-size) */
  overflow: hidden;
  position: relative;
`;

const DynamicList = styled.div`
  display: flex;
  flex-direction: column;
  will-change: transform;
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const Word = styled.div`
  height: 1.2em; /* match viewport height */
  display: flex;
  align-items: center;
  font-weight: 800;
  font-size: 8vw; /* same size as TextRow for consistency */
  color: #9281dd;
  line-height: 1;
`;

export default function ManageYour() {
  const words = [
    "Goals",
    "Disciple",
    "Tasks",
    "Progress",
    "Finances",
    "Time",
    "Growth",
  ];
  const sectionRef = useRef(null);
  const viewportRef = useRef(null);

  const [rowHeight, setRowHeight] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const setHeights = () => {
      const h = viewportRef.current ? viewportRef.current.clientHeight : 0;
      setRowHeight(h || 1);
    };
    setHeights();
    window.addEventListener("resize", setHeights);
    return () => window.removeEventListener("resize", setHeights);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let lastIndex = -1;

    const handle = () => {
      const scrollTop = window.scrollY || window.pageYOffset;
      const rect = el.getBoundingClientRect();
      const sectionTop = rect.top + scrollTop;
      const sectionHeight = el.offsetHeight;
      const maxScroll = Math.max(sectionHeight - window.innerHeight, 1);

      const scrolled = Math.min(Math.max(scrollTop - sectionTop, 0), maxScroll);
      const progress = scrolled / maxScroll;

      const floatIndex = progress * words.length;
      let idx = Math.floor(floatIndex);

      if (idx < 0) idx = 0;
      if (idx > words.length - 1) idx = words.length - 1;

      if (idx !== lastIndex) {
        lastIndex = idx;
        setActiveIndex(idx);
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(handle);
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [words.length]);

  // 🔥 Faster transitions: 60vh per word
  const vhHeight = `${(words.length + 1) * 33}vh`;

  return (
    <Section ref={sectionRef} $vhHeight={vhHeight}>
      <StickyContainer>
        <TextRow>
          <Static>Track Your</Static>

          <DynamicViewport ref={viewportRef}>
            <DynamicList
              style={{
                transform: `translateY(-${activeIndex * rowHeight}px)`,
              }}
            >
              {words.map((w, i) => (
                <Word key={i}>{w}</Word>
              ))}
            </DynamicList>
          </DynamicViewport>
        </TextRow>
      </StickyContainer>
    </Section>
  );
}
